import { type ParsedMarkdownTableData } from './markdown-table.util';

/** Matches compact legacy regulation IDs only (e.g. INS2571). Tenant-scoped IDs (INS_1436_2671) are excluded. */
export const INS_ID_TOKEN_REGEX = /\b(INS\d+)\b/gi;

/** Compact source ids and persisted tenant ids (e.g. INS_6334_130316) for display links. */
export const LINKABLE_INS_ID_TOKEN_REGEX = /\b(INS\d+|INS_\d+_\d+)\b/gi;

/** Tenant-scoped regulation IDs (e.g. INS_6334_1228) — protected during markdown stripping only. */
const TENANT_SCOPED_INS_ID_REGEX = /\bINS_\d+_\d+\b/gi;

const INS_PLACEHOLDER_OPEN = '\uE000';
const INS_PLACEHOLDER_CLOSE = '\uE001';

function protectInsIdMatches(
  text: string,
  regex: RegExp,
  placeholders: Map<string, string>,
  counter: { value: number },
): string {
  return text.replace(regex, (match) => {
    const token = `${INS_PLACEHOLDER_OPEN}${counter.value++}${INS_PLACEHOLDER_CLOSE}`;
    placeholders.set(token, match);
    return token;
  });
}

/** Temporarily replaces INS tokens so markdown stripping does not alter underscores. */
export function protectInsIdsInText(value: string): {
  text: string;
  placeholders: ReadonlyMap<string, string>;
} {
  const placeholders = new Map<string, string>();
  const counter = { value: 0 };
  let text = protectInsIdMatches(value, TENANT_SCOPED_INS_ID_REGEX, placeholders, counter);
  text = protectInsIdMatches(text, INS_ID_TOKEN_REGEX, placeholders, counter);
  return { text, placeholders };
}

/** Restores INS tokens after markdown stripping. */
export function restoreProtectedInsIds(
  value: string,
  placeholders: ReadonlyMap<string, string>,
): string {
  let result = value;
  for (const [token, original] of placeholders) {
    result = result.split(token).join(original);
  }
  return result;
}

function collectInsIds(text: string, regex: RegExp): readonly string[] {
  const ids = new Set<string>();
  const matcher = new RegExp(regex.source, regex.flags);
  for (const match of text.matchAll(matcher)) {
    const id = (match[1] || '').toUpperCase();
    if (id) {
      ids.add(id);
    }
  }
  return Array.from(ids);
}

/** True for compact legacy regulation IDs only (e.g. INS2571). */
export function isCompactLegacyInsId(insId: string): boolean {
  return /^INS\d+$/i.test(insId.trim());
}

/** Collects unique compact INS IDs from plain text (case-normalized to uppercase). */
export function extractInsIdsFromText(text: string): readonly string[] {
  return collectInsIds(text, INS_ID_TOKEN_REGEX);
}

/** Collects compact and tenant-scoped INS IDs used for display linkification. */
export function extractLinkableInsIdsFromText(text: string): readonly string[] {
  return collectInsIds(text, LINKABLE_INS_ID_TOKEN_REGEX);
}

/** Collects unique INS IDs from table row cells. */
export function extractInsIdsFromTableData(tableData: ParsedMarkdownTableData): readonly string[] {
  const ids = new Set<string>();
  for (const row of tableData.rows) {
    for (const cell of row) {
      extractInsIdsFromText(cell ?? '').forEach((id) => ids.add(id));
    }
  }
  return Array.from(ids);
}

/** Replaces INS ID tokens in text using a normalized (uppercase) lookup map. */
export function replaceInsIdsInText(text: string, mappings: ReadonlyMap<string, string>): string {
  return text.replace(INS_ID_TOKEN_REGEX, (fullMatch) => {
    const normalized = fullMatch.toUpperCase();
    return mappings.get(normalized) ?? fullMatch;
  });
}

/** Returns a copy of table row data with INS IDs replaced per the mapping. */
export function mapTableDataInsIds(
  tableData: ParsedMarkdownTableData,
  mappings: ReadonlyMap<string, string>,
): ParsedMarkdownTableData {
  if (mappings.size === 0) {
    return tableData;
  }
  return {
    columns: tableData.columns,
    rows: tableData.rows.map((row) =>
      row.map((cell) => replaceInsIdsInText(cell ?? '', mappings)),
    ),
  };
}
